'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { assert, cn } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthApi, AuthMutation } from '@/api';
import { BREAKPOINTS, DONATION_ROUTES, ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { openResponsiveDialog } from '@/stores/DrawerStore';
import { getActionsForStatus, ORDER_PRODUCT_ACTION_LABELS, ORDER_PRODUCT_ACTIONS } from '../constants';
import type { OrderProductAction } from '../types';
import { CouponCancelModal } from './CouponCancelModal';
import { CouponDetailDrawer } from './CouponDetailDrawer';
import { DeliveryTrackingModalContent } from './DeliveryTrackingModalContent';
import { PurchaseConfirmModal } from './PurchaseConfirmModal';
interface ProductActionsProps {
  className?: string;
  orderId: string;
  orderNo: string;
  productId: string;
  reviewProductId?: string;
  orderProductStatus?: string;
  isOnlineProduct?: boolean;
  hasReview?: boolean;
  returnId?: string;
  orderType?: string;
  couponCode?: string;
  deliveryId?: string;
  trackingProvider?: 'GOODSFLOW' | 'LOGIS_TRACKING';
}
export const ProductActions = t0 => {
  const $ = _c(89);
  const {
    className,
    orderId,
    orderNo,
    productId,
    reviewProductId,
    orderProductStatus,
    isOnlineProduct,
    hasReview,
    returnId,
    orderType,
    couponCode,
    deliveryId,
    trackingProvider
  } = t0;
  const [isPurchaseConfirmModalOpened, setIsPurchaseConfirmModalOpened] = useState(false);
  const [isCouponCancelConfirmModalOpened, setIsCouponCancelConfirmModalOpened] = useState(false);
  const [selectedCouponCode, setSelectedCouponCode] = useState("");
  const [isCancelClaimModalOpened, setIsCancelClaimModalOpened] = useState(false);
  const [isReturnConfirmModalOpened, setIsReturnConfirmModalOpened] = useState(false);
  const router = useCustomRouter();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthMutation["put:/wello/v2/auth/home/gift/order/{orderId}/status"]();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== queryClient || $[2] !== toast) {
    t2 = async () => {
      await queryClient.invalidateQueries();
      toast({
        message: "\uC8FC\uBB38\uC0C1\uD0DC\uAC00 \uBCC0\uACBD\uB418\uC5C8\uC5B4\uC694"
      });
    };
    $[1] = queryClient;
    $[2] = toast;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== toast) {
    t3 = () => {
      toast({
        type: "fail",
        message: "\uAD6C\uB9E4 \uD655\uC815\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694."
      });
    };
    $[4] = toast;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      ...t1,
      onSuccess: t2,
      onError: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    mutate: confirmGiftPurchase
  } = useMutation(t4);
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = AuthMutation["post:/wello/v2/auth/point-market/order/reissue-coupon"]();
    t6 = t7 => {
      const {
        context
      } = t7;
      assert(context?.couponCode);
      setSelectedCouponCode(context.couponCode);
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== toast) {
    t7 = {
      ...t5,
      onSuccess: t6,
      onError: () => {
        toast({
          type: "fail",
          message: "\uCFE0\uD3F0 \uCF54\uB4DC\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694."
        });
      }
    };
    $[11] = toast;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const reissueCouponMutation = useMutation(t7);
  let t8;
  if ($[13] !== hasReview || $[14] !== isOnlineProduct || $[15] !== orderProductStatus || $[16] !== orderType) {
    t8 = getActionsForStatus(orderProductStatus, isOnlineProduct, hasReview, orderType);
    $[13] = hasReview;
    $[14] = isOnlineProduct;
    $[15] = orderProductStatus;
    $[16] = orderType;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const actions = t8;
  let t9;
  if ($[18] !== confirmGiftPurchase || $[19] !== couponCode || $[20] !== deliveryId || $[21] !== orderId || $[22] !== orderNo || $[23] !== orderType || $[24] !== productId || $[25] !== reissueCouponMutation || $[26] !== reviewProductId || $[27] !== router || $[28] !== trackingProvider) {
    t9 = action => {
      bb10: switch (action) {
        case ORDER_PRODUCT_ACTIONS.PURCHASE_CONFIRM:
          {
            if (orderType === "GIFT") {
              confirmGiftPurchase({
                path: {
                  orderId
                },
                body: {
                  status: "PURCHASE_CONFIRMED",
                  reason: "\uAD6C\uB9E4\uD655\uC815"
                }
              });
            } else {
              setIsPurchaseConfirmModalOpened(true);
            }
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING:
          {
            if (orderType === "GIFT") {
              router.push(`${DONATION_ROUTES.DONATION_MY_ORDERS_DETAIL.pathname({
                id: orderId
              })}#delivery-info`);
            } else {
              if (deliveryId) {
                openResponsiveDialog({
                  modalHeader: {
                    title: "\uBC30\uC1A1 \uC870\uD68C"
                  },
                  title: "\uBC30\uC1A1 \uC870\uD68C",
                  drawerType: "bottom",
                  component: DeliveryTrackingModalContent,
                  props: {
                    orderId,
                    deliveryId,
                    trackingProvider
                  }
                });
              }
            }
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.CANCEL:
          {
            router.push(ROUTES.MY_ORDERS_CANCEL.withSearchParams({
              dynamicPath: {
                orderNo
              },
              searchParams: {
                productId,
                orderId
              }
            }));
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.RETURN:
          {
            setIsReturnConfirmModalOpened(true);
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.REVIEW_WRITE:
          {
            router.push(ROUTES.MY_ORDERS_REVIEW_CREATE.withSearchParams({
              dynamicPath: {
                orderNo
              },
              searchParams: {
                productId: reviewProductId ?? productId,
                orderId,
                orderType: orderType === "GIFT" ? "GIFT" : undefined
              }
            }));
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.REPURCHASE:
          {
            router.push(WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
              productNumber: productId
            }));
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.MOBILE_COUPON_CANCEL:
          {
            setIsCouponCancelConfirmModalOpened(true);
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.USING:
          {
            if (couponCode) {
              setSelectedCouponCode(couponCode);
            } else {
              reissueCouponMutation.mutate({
                body: {
                  orderIdIdx: orderId
                }
              });
            }
            break bb10;
          }
        case ORDER_PRODUCT_ACTIONS.CANCEL_CLAIM:
          {
            setIsCancelClaimModalOpened(true);
            break bb10;
          }
        default:
      }
    };
    $[18] = confirmGiftPurchase;
    $[19] = couponCode;
    $[20] = deliveryId;
    $[21] = orderId;
    $[22] = orderNo;
    $[23] = orderType;
    $[24] = productId;
    $[25] = reissueCouponMutation;
    $[26] = reviewProductId;
    $[27] = router;
    $[28] = trackingProvider;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleActionClick = t9;
  if (actions.length === 0) {
    return null;
  }
  let t10;
  if ($[30] !== className) {
    t10 = cn("w-240", "mobile-tablet:w-full", className);
    $[30] = className;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("flex flex-col gap-8", "mobile-tablet:hidden");
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== actions[0] || $[34] !== handleActionClick) {
    t12 = () => handleActionClick(actions[0]);
    $[33] = actions[0];
    $[34] = handleActionClick;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = ORDER_PRODUCT_ACTION_LABELS[actions[0]];
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = <Button theme="sub-line" dimensions="H40" className="w-full !rounded-none" onClick={t12}>{t13}</Button>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== actions || $[40] !== handleActionClick) {
    t15 = actions.length > 1 ? <div className="flex gap-8">{actions.slice(1).map(action_0 => <Button key={action_0} theme="sub-line" dimensions="H40" className="flex-1 !rounded-none" onClick={() => handleActionClick(action_0)}>{ORDER_PRODUCT_ACTION_LABELS[action_0]}</Button>)}</div> : null;
    $[39] = actions;
    $[40] = handleActionClick;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t14 || $[43] !== t15) {
    t16 = <div className={t11}>{t14}{t15}</div>;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn("hidden w-full flex-wrap gap-4", "mobile-tablet:flex");
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== actions || $[47] !== handleActionClick) {
    let t19;
    if ($[49] !== handleActionClick) {
      t19 = action_1 => <Button key={action_1} theme="sub-line" dimensions="H32" className="flex-1 !rounded-none" onClick={() => handleActionClick(action_1)}>{ORDER_PRODUCT_ACTION_LABELS[action_1]}</Button>;
      $[49] = handleActionClick;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    t18 = actions.map(t19);
    $[46] = actions;
    $[47] = handleActionClick;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[51] !== t18) {
    t19 = <div className={t17}>{t18}</div>;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsPurchaseConfirmModalOpened(false);
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== isPurchaseConfirmModalOpened || $[55] !== orderId || $[56] !== orderNo || $[57] !== productId) {
    t21 = <PurchaseConfirmModal orderId={orderId} orderNo={orderNo} productId={productId} opened={isPurchaseConfirmModalOpened} onClose={t20} />;
    $[54] = isPurchaseConfirmModalOpened;
    $[55] = orderId;
    $[56] = orderNo;
    $[57] = productId;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setIsCouponCancelConfirmModalOpened(false);
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== isCouponCancelConfirmModalOpened) {
    t23 = <CouponCancelModal opened={isCouponCancelConfirmModalOpened} onClose={t22} />;
    $[60] = isCouponCancelConfirmModalOpened;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const t24 = !!selectedCouponCode;
  let t25;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setSelectedCouponCode("");
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== selectedCouponCode || $[64] !== t24) {
    t26 = <CouponDetailDrawer opened={t24} onClose={t25} couponCode={selectedCouponCode} />;
    $[63] = selectedCouponCode;
    $[64] = t24;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setIsCancelClaimModalOpened(false);
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== isCancelClaimModalOpened || $[68] !== orderId || $[69] !== returnId) {
    t28 = <CancelReturnModal orderId={orderId} returnId={returnId} opened={isCancelClaimModalOpened} onClose={t27} />;
    $[67] = isCancelClaimModalOpened;
    $[68] = orderId;
    $[69] = returnId;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsReturnConfirmModalOpened(false);
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== orderId || $[73] !== orderNo || $[74] !== productId || $[75] !== router) {
    t30 = () => {
      router.push(ROUTES.MY_ORDERS_RETURN.withSearchParams({
        dynamicPath: {
          orderNo
        },
        searchParams: {
          productId,
          orderId
        }
      }));
      setIsReturnConfirmModalOpened(false);
    };
    $[72] = orderId;
    $[73] = orderNo;
    $[74] = productId;
    $[75] = router;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== isReturnConfirmModalOpened || $[78] !== t30) {
    t31 = <ReturnConfirmModal opened={isReturnConfirmModalOpened} onClose={t29} onConfirm={t30} />;
    $[77] = isReturnConfirmModalOpened;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t10 || $[81] !== t16 || $[82] !== t19 || $[83] !== t21 || $[84] !== t23 || $[85] !== t26 || $[86] !== t28 || $[87] !== t31) {
    t32 = <div className={t10}>{t16}{t19}{t21}{t23}{t26}{t28}{t31}</div>;
    $[80] = t10;
    $[81] = t16;
    $[82] = t19;
    $[83] = t21;
    $[84] = t23;
    $[85] = t26;
    $[86] = t28;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  return t32;
};

// ─── 반품 요청 취소 확인 모달 ─────────────────────────────────────────────────

interface CancelReturnModalProps {
  orderId: string;
  returnId?: string;
  opened: boolean;
  onClose: () => void;
}
const CancelReturnModal = t0 => {
  const $ = _c(32);
  const {
    orderId,
    returnId,
    opened,
    onClose
  } = t0;
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  let t1;
  if ($[0] !== orderId || $[1] !== returnId) {
    t1 = () => AuthApi["put:/wello/v2/auth/local-market-orders/{orderId}/return/{returnId}/cancel"]({
      path: {
        orderId,
        returnId
      }
    });
    $[0] = orderId;
    $[1] = returnId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onClose || $[4] !== queryClient || $[5] !== toast) {
    t2 = async () => {
      await queryClient.invalidateQueries();
      toast({
        message: "\uBC18\uD488 \uC694\uCCAD\uC774 \uCDE8\uC18C\uB418\uC5C8\uC5B4\uC694."
      });
      onClose();
    };
    $[3] = onClose;
    $[4] = queryClient;
    $[5] = toast;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== toast) {
    t3 = () => {
      toast({
        type: "fail",
        message: "\uBC18\uD488 \uC694\uCCAD \uCDE8\uC18C\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694."
      });
    };
    $[7] = toast;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    mutateAsync: cancelReturn,
    isPending
  } = useMutation(t4);
  if (!opened) {
    return null;
  }
  const t5 = isDesktop ? "H56" : "H48";
  let t6;
  if ($[13] !== isPending || $[14] !== onClose || $[15] !== t5) {
    t6 = <Button theme="sub-fill" dimensions={t5} onClick={onClose} className="flex-1 whitespace-nowrap" disabled={isPending}>닫기</Button>;
    $[13] = isPending;
    $[14] = onClose;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = isDesktop ? "H56" : "H48";
  let t8;
  if ($[17] !== cancelReturn) {
    t8 = () => cancelReturn();
    $[17] = cancelReturn;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isPending || $[20] !== t7 || $[21] !== t8) {
    t9 = <Button theme="primary-fill" dimensions={t7} className="flex-1 whitespace-nowrap" onClick={t8} disabled={isPending}>요청 취소</Button>;
    $[19] = isPending;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t6 || $[24] !== t9) {
    t10 = <div className="flex gap-8">{t6}{t9}</div>;
    $[23] = t6;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p> </p>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="max-w-[26rem] pt-12"><p className={cn("pb-16 font-800 text-Gray-900 typo-h5", "mobile-tablet:pb-8 mobile-tablet:font-700 mobile-tablet:typo-body2")}>반품 요청 취소</p><p className={cn("font-400 text-Gray-700 typo-body2", "mobile-tablet:typo-body3")}>반품 요청을 취소하시겠습니까?</p></div>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== onClose || $[29] !== opened || $[30] !== t10) {
    t13 = <Modal opened={opened} onClose={onClose} closeButton={true} buttonFooter={t10} header={t11}>{t12}</Modal>;
    $[28] = onClose;
    $[29] = opened;
    $[30] = t10;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};

// ─── 반품 확인 모달 ──────────────────────────────────────────────────────────

interface ReturnConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
}
const ReturnConfirmModal = t0 => {
  const $ = _c(14);
  const {
    opened,
    onClose,
    onConfirm
  } = t0;
  if (!opened) {
    return null;
  }
  let t1;
  if ($[0] !== onClose) {
    t1 = <Button theme="sub-fill" dimensions="H56" onClick={onClose} className="flex-1 whitespace-nowrap">취소</Button>;
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onConfirm) {
    t2 = <Button theme="primary-fill" dimensions="H56" className="flex-1 whitespace-nowrap" onClick={onConfirm}>진행할게요</Button>;
    $[2] = onConfirm;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex gap-8">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p> </p>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="font-800 text-Gray-900 typo-h5">반품 하시겠습니까?</p>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-col items-center gap-16 pt-12">{t5}<div className="flex flex-col items-center gap-8"><p className="text-center font-400 text-Gray-700 typo-body2">상품 특성이나 상태에 따라<br />반품이 안될 수도 있어요.</p><p className="font-400 text-Gray-550 typo-body4">(예) 포장 훼손, 주문제작된 상품</p></div></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onClose || $[11] !== opened || $[12] !== t3) {
    t7 = <Modal opened={opened} onClose={onClose} closeButton={true} buttonFooter={t3} header={t4}>{t6}</Modal>;
    $[10] = onClose;
    $[11] = opened;
    $[12] = t3;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};